import {
  type IoConfig,
  type PartialPropsOf,
  type PropsOf,
  type WithIoConfig,
  type WithProps,
} from '@blaze-runtime/engine-domain';
import { type FontWeightOption } from '@blaze-shared/api-contracts-domain';
import {
  type ElementSizingMode,
  type IconSelectOption,
  type PropertiesConfig,
  type PropertiesConfigInputOptionsSelect,
  type PropertiesConfigItemDefinition,
} from '@blaze-shared/elements-domain';
import { type SelectOption } from '@blaze-shared/shared-util';

import { registerElementDefinition } from '../../services/element-definitions.service';
import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import { BreadcrumbsPropsBuilder } from './breadcrumbs-props.builder';
import { BreadcrumbsComponent } from './breadcrumbs.component';
import { type BreadcrumbsPropsModel, type LabelState } from './breadcrumbs.models';
import { breadcrumbsV2PaddingToMarginMigration } from './migrations/002-breadcrumbs-padding-to-margin.migration';

export type BreadcrumbsDef = WithProps<BreadcrumbsPropsModel> & WithIoConfig;

declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    breadcrumbs: BreadcrumbsDef;
  }
}

const DEFAULT_HEIGHT = 48;

const MIN_SIZE = 16;
const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['min'];

registerElementDefinition({
  type: 'breadcrumbs',
  componentType: BreadcrumbsComponent,
  isListed: true,
  name: 'Breadcrumbs',
  groups: ['navigation'],
  width: { mode: 'full', fixed: true },
  height: { mode: 'value', value: DEFAULT_HEIGHT },
  ioConfig: getIoConfig(),
  propsConfig: getPropsConfig(),
  defaults: getDefaults(),
  initial: getInitialProps(),
  propsBuilder: BreadcrumbsPropsBuilder,
  styleIncludePaths: ['style'],
  features: {
    preventAutoHeight: true,
  },
  version: 2,
  migrations: {
    2: breadcrumbsV2PaddingToMarginMigration,
  },
});

function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'value',
        isDefaultDataSourceInput: true,
        isDefaultDataValueInput: true,
        bindingDefs: [],
      },
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
  };
}

function getInitialProps(): PartialPropsOf<'breadcrumbs'> {
  return {
    content: {
      mode: 'drilldown',
    },
    style: {
      label: {
        normal: {
          color: { color1: '$t.title', selectorType: 'solid' },
          fontFamily: '$t.fontFamily',
          fontWeight: '$t.labelFontWeight' as FontWeightOption,
          style: [],
          fontSize: '$t.fontSize',
        },
        hover: {
          color: { color1: '$t.accent', selectorType: 'solid' },
          fontFamily: '$t.fontFamily',
          fontWeight: '$t.labelFontWeight' as FontWeightOption,
          style: [],
          fontSize: '$t.fontSize',
        },
        active: {
          color: { color1: '$t.title', selectorType: 'solid' },
          fontFamily: '$t.fontFamily',
          fontWeight: '700',
          style: [],
          fontSize: '$t.fontSize',
        },
      },
      offset: { margin: '$t.spacingInput' },
      labelState: 'normal',
    },
  };
}

function getDefaults(): PropsOf<'breadcrumbs'> {
  return {
    content: {
      mode: 'drilldown',
    },
    style: {
      label: {
        normal: {
          color: { color1: '$t.title', selectorType: 'solid' },
          fontFamily: '$t.fontFamily',
          fontWeight: '$t.labelFontWeight' as FontWeightOption,
          style: [],
          fontSize: '$t.fontSize',
        },
        hover: {
          color: { color1: '$t.accent', selectorType: 'solid' },
          fontFamily: '$t.fontFamily',
          fontWeight: '700',
          style: [],
          fontSize: '$t.fontSize',
        },
        active: {
          color: { color1: '$t.title', selectorType: 'solid' },
          fontFamily: '$t.fontFamily',
          fontWeight: '700',
          style: [],
          fontSize: '$t.fontSize',
        },
      },
      divider: {
        type: 'chevron',
        size: '16px',
        color: { color1: '$t.title', selectorType: 'solid' },
      },
      offset: { margin: '$t.spacingInput' },
      labelState: 'normal',
    },
  };
}

function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('breadcrumbs', [
    epc.section('content', 'Content', [
      epc.subsections.visibilitySubsection(),
      epc.subsections.subsection('content.breadcrumbs', 'Size', [
        epc.root.elementWidthField(
          { min: MIN_SIZE, max: MAX_WIDTH, unit: 'px', step: 1 },
          WIDTH_OPTIONS,
        ),
        epc.root.elementHeightField(
          { min: MIN_SIZE, max: MAX_HEIGHT, unit: 'px', step: 1 },
          HEIGHT_OPTIONS,
        ),
      ]),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),

        epc.subsections.subsection('style', 'Margin', [
          epc.fields.spacingAndAlignField('style.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.label', 'Label', [
          epc.fields.radiosField(
            'style.labelState',
            'State',
            [
              <IconSelectOption<LabelState>>{ value: 'normal', label: 'Default' },
              <IconSelectOption<LabelState>>{ value: 'hover', label: 'Hover' },
              <IconSelectOption<LabelState>>{ value: 'active', label: 'Active' },
            ],
            { hideLabel: true },
          ),
          ...labelStyleFields('normal'),
          ...labelStyleFields('hover'),
          ...labelStyleFields('active'),
        ]),

        epc.subsections.subsection('style.divider', 'Divider', [
          dividerTypeField(),
          epc.fields.colorField(`style.divider.color`, 'Color', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.sliderField(`style.divider.size`, 'Size', [{ min: 8, max: 72, unit: 'px' }]),
        ]),
      ],
      true,
    ),
  ]);
}

function labelStyleFields(state: LabelState): PropertiesConfigItemDefinition[] {
  return epc.utils.conditionalFields(
    [
      epc.fields.colorField(`style.label.${state}.color`, 'Color', {
        allowGradient: false,
        allowNone: false,
      }),
      ...epc.fields.fontFamilyAndWeightFields(
        `style.label.${state}.fontFamily`,
        `style.label.${state}.fontWeight`,
      ),
      epc.fields.fontStyleField(`style.label.${state}.style`, 'Style', ['line-through', 'bold']),
      epc.fields.sliderField(`style.label.${state}.fontSize`, 'Size', [
        { min: 8, max: 72, unit: 'px' },
      ]),
    ],
    'style.labelState',
    (labelState: LabelState) => labelState !== state,
  );
}

function dividerTypeField(): PropertiesConfigItemDefinition<'select'> {
  const inputOpts: Partial<PropertiesConfigInputOptionsSelect> = {
    showSelection: true,
    layoutMode: 'normal',
    hideLabel: true,
    defaultValue: 'chevron',
    showNoneOption: false,
  };

  const options = [
    { value: 'chevron', label: 'Chevron' },
    { value: 'arrow', label: 'Arrow' },
    { value: 'dash', label: 'Dash' },
  ] as SelectOption[];

  return epc.fields.selectFieldFromList('style.divider.type', 'Type', options, inputOpts);
}
